import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LanguageProvider } from './i18n/LanguageContext';
import SiteNav from './components/SiteNav';
import SiteFooter from './components/SiteFooter';
import HomePage from './pages/HomePage';
import WorkPage from './pages/WorkPage';
import './styles/redesign.css';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'SIA GDWD',
  url: 'https://gdwd.lv',
  email: 'info@gdwd.lv',
  description:
    'SIA GDWD — products, operational platforms, mobile apps and long-term systems for education and public partners.',
  sameAs: [
    'https://github.com/guntarsdikis',
    'https://www.linkedin.com/in/guntars-dikis/',
  ],
};

function RouteScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    if (hash.startsWith('#case-')) return undefined;

    let cancelled = false;
    const timeoutIds = [];
    const frameIds = [];

    const applyScroll = () => {
      if (cancelled) return;

      if (!hash) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        return;
      }

      const id = decodeURIComponent(hash.slice(1));
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    };

    const scheduleScroll = (delay) => {
      const timeoutId = window.setTimeout(() => {
        const frameId = window.requestAnimationFrame(applyScroll);
        frameIds.push(frameId);
      }, delay);
      timeoutIds.push(timeoutId);
    };

    scheduleScroll(0);
    scheduleScroll(80);
    scheduleScroll(240);

    return () => {
      cancelled = true;
      frameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [pathname, hash]);

  return null;
}

function Shell() {
  useEffect(() => {
    document.body.classList.add('gdwd-redesign');
    return () => document.body.classList.remove('gdwd-redesign');
  }, []);

  return (
    <div className="gdwd-shell">
      <Helmet>
        <title>GDWD — Products &amp; operational platforms</title>
        <meta
          name="description"
          content="GDWD designs and engineers production software — products, operational platforms, mobile apps, and long-term systems for education and public partners."
        />
        <link rel="canonical" href="https://gdwd.lv" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <RouteScrollManager />
      <SiteNav />
      <main id="main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/work" element={<WorkPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>
      <SiteFooter />
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </LanguageProvider>
  );
}
